import { useEffect, useRef, useState, type CSSProperties, type HTMLAttributes, type PointerEvent, type ReactNode } from 'react'
import './chartPaneLayout.css'

interface ChartPaneFrameProps {
  id: string
  label: string
  width: number
  onWidthChange: (width: number) => void
  dropProps: HTMLAttributes<HTMLDivElement>
  orderControls: ReactNode
  children: ReactNode
}

/** Grid placement belongs to a pane, independently of its exchange or metric. */
export function ChartPaneFrame({ id, label, width, onWidthChange, dropProps, orderControls, children }: ChartPaneFrameProps) {
  const [previewWidth, setPreviewWidth] = useState<number | null>(null)
  const displayWidth = previewWidth ?? width
  return (
    <div {...dropProps}
      className={`screener-chart-pane min-w-0 ${dropProps.className || ''}`}
      style={{ '--chart-columns': displayWidth } as CSSProperties}
      data-chart-pane={id}
      data-exchange-chart={id.startsWith('oi:') ? id.slice(3) : undefined}
      data-cvd-chart={id.startsWith('cvd:') ? id.slice(4) : undefined}>
      <div className="mb-1 flex min-h-6 flex-wrap items-center justify-between gap-1 px-1">
        {orderControls}
      </div>
      {children}
      <ChartWidthHandle label={label} width={displayWidth} onPreview={setPreviewWidth}
        onCommit={(next) => { setPreviewWidth(null); if (next !== width) onWidthChange(next) }} />
    </div>
  )
}

const clampWidth = (value: number) => Math.min(100, Math.max(1, Math.round(value)))

function ChartWidthHandle({ label, width, onPreview, onCommit }: {
  label: string
  width: number
  onPreview: (width: number | null) => void
  onCommit: (width: number) => void
}) {
  const dragRef = useRef<{
    pointerId: number
    startX: number
    startWidth: number
    rowWidth: number
    width: number
    restore: () => void
  } | null>(null)
  const frameRef = useRef<number | null>(null)
  const [dragging, setDragging] = useState(false)

  useEffect(() => () => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    const drag = dragRef.current
    dragRef.current = null
    drag?.restore()
  }, [])

  const moveDrag = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || event.pointerId !== drag.pointerId) return
    drag.width = clampWidth(drag.startWidth + (event.clientX - drag.startX) / drag.rowWidth * 100)
    if (frameRef.current === null) frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null
      if (dragRef.current) onPreview(dragRef.current.width)
    })
  }

  const finishDrag = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || event.pointerId !== drag.pointerId) return
    if (event.type === 'pointerup') {
      drag.width = clampWidth(drag.startWidth + (event.clientX - drag.startX) / drag.rowWidth * 100)
    }
    dragRef.current = null
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    frameRef.current = null
    drag.restore()
    setDragging(false)
    // A cancelled gesture must not overwrite a saved layout.
    if (event.type === 'pointerup') onCommit(drag.width)
    else onPreview(null)
  }

  return (
    <div role="separator" aria-label={`Resize ${label} width`} aria-orientation="vertical"
      aria-valuemin={1} aria-valuemax={100} aria-valuenow={width} aria-valuetext={`${width}% of row`}
      tabIndex={0} title="Drag to resize chart width · Double-click to fill the row"
      className={`screener-chart-width-handle group absolute inset-y-1 right-0 z-20 w-2.5 cursor-col-resize touch-none select-none items-center justify-center rounded outline-none hover:bg-teal-500/15 focus-visible:bg-teal-500/20 ${dragging ? 'bg-teal-500/20' : ''}`}
      onPointerDown={(event) => {
        if (event.button !== 0 || !event.isPrimary || dragRef.current) return
        const target = event.currentTarget
        const rowWidth = target.closest('[data-chart-grid]')?.getBoundingClientRect().width ?? 0
        if (rowWidth <= 0) return
        event.preventDefault()
        event.stopPropagation()
        const pointerId = event.pointerId
        target.focus({ preventScroll: true })
        target.setPointerCapture(pointerId)
        const { cursor, userSelect } = document.body.style
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
        dragRef.current = {
          pointerId, startX: event.clientX, startWidth: width, rowWidth, width,
          restore: () => {
            document.body.style.cursor = cursor
            document.body.style.userSelect = userSelect
            if (target.hasPointerCapture(pointerId)) target.releasePointerCapture(pointerId)
          },
        }
        setDragging(true)
      }}
      onPointerMove={moveDrag} onPointerUp={finishDrag} onPointerCancel={finishDrag} onLostPointerCapture={finishDrag}
      onDoubleClick={(event) => { event.stopPropagation(); onCommit(100) }}
      onKeyDown={(event) => {
        if (dragRef.current || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
        event.preventDefault()
        event.stopPropagation()
        const step = event.shiftKey ? 10 : 1
        onCommit(clampWidth(event.key === 'Home' ? 1 : event.key === 'End' ? 100
          : width + (event.key === 'ArrowRight' ? step : -step)))
      }}>
      <span className={`h-10 w-0.5 rounded-full group-hover:bg-teal-400 group-focus-visible:bg-teal-400 ${dragging ? 'bg-teal-400' : 'bg-slate-600/70'}`} />
    </div>
  )
}
